<template>
	<div class="sub_contents">
		<div class="cont_center">
			<h1 class="title_page">자주하는 질문</h1>

			<!-- 검색 -->
			<div class="search_box_area faq">
				<div class="input_area notice">
					<InputText v-model="searchKeyword" :placeholder="placeholderSearch" @keyup.enter="searchFaq" />
					<button type="button" class="btn_search" @click="searchFaq">검색</button>
				</div>
			</div>
			<!-- //검색 -->

			<p v-if="showBenefitGuide" class="card_benefit_link">
				<RouterLink to="/HFO_HI_002">복지카드 청구할인 혜택 정리 보기</RouterLink>
			</p>

			<div class="faq_area">
				<TabArea :tab="tab" :currentTab="currentTab" :class="{ tab_col_3: isTabActive }" @click="changeTab" />

				<div class="tab_contents">
					<p class="search_result_num">
						검색 결과 총 <strong>{{ total }}</strong>개
					</p>

					<!-- 로딩 상태 표시 -->
					<div v-if="faqData.isLoading.value" class="loading_area">
						<p>데이터를 불러오는 중...</p>
					</div>

					<!-- 에러 상태 표시 -->
					<div v-else-if="faqData.isError.value" class="error_area">
						<p>데이터를 불러오는 중 오류가 발생했습니다.</p>
						<button @click="faqData.refetch()" class="btn_retry">다시 시도</button>
					</div>

					<!-- FAQ 리스트 -->
					<ul class="faq_list" v-else-if="faqList.length > 0">
						<li :key="item.faqUuid || index" v-for="(item, index) in faqList">
							<div class="faq_top" @click="openFaq(index)" :class="{ open: item.isOpen }">
								<span class="bedge_q">Q</span>

								<h3 class="title_f">
									{{ item.faqTitle }}
									<span class="bedge_basic" v-if="item.bestYn === 'Y'">BEST</span>
								</h3>
							</div>

							<div class="faq_contents">
								<span class="bedge_a">A</span>
								<div class="answer_content" v-html="item.rmks"></div>
							</div>
						</li>
					</ul>

					<!-- 데이터 없음 -->
					<div v-else class="no_data_area">
						<p>검색 결과가 없습니다.</p>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import InputText from "@/components/InputText";
import TabArea from "@/components/TabArea";
import { ref, watch, onMounted, nextTick } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { getAxios } from "@/lib/AxiosCall";
import { useRouter } from "vue-router";
import { currentProfile, setJsonLd, stripHtml } from "@/lib/Seo";

export default {
	components: {
		InputText,
		TabArea,
	},
	data() {
		return {
			placeholderSearch: "검색어를 입력해 주세요.",
			searchKeyword: "",
			currentTab: 0,
			tab: [
				{
					name: "전체",
					cd: "",
				},
				{
					name: "서비스 안내",
					cd: "FAQ_SERVICE",
				},
				{
					name: "가맹점",
					cd: "FAQ_STORE",
				},
				{
					name: "혜택",
					cd: "FAQ_BENEFIT",
				},
				{
					name: "결제",
					cd: "FAQ_PAYMENT",
				},
				{
					name: "기타",
					cd: "FAQ_ETC",
				},
			],
			isOpen: false,
			isTabActive: true,
			showBenefitGuide: Boolean(currentProfile().benefitGuide),
		};
	},
	setup() {
		const router = useRouter();

		// faq
		const faqList = ref([]);
		const currentPage = ref(1);
		const pageSize = ref(8);
		const total = ref(0);

		const faqReqData = ref({
			page: currentPage.value,
			size: pageSize.value,
			title: "",
			faqType: "", // 전체 탭 (빈 문자열)
		});

		console.log("초기 faqReqData:", faqReqData.value);

		const faqData = useQuery({
			queryKey: ["faq-list", faqReqData.value],
			queryFn: async () => {
				console.log("FAQ API 호출:", faqReqData.value);
				const result = await getAxios("/hvm/v1/help/faq", faqReqData.value);
				console.log("FAQ API 응답:", result);
				return result;
			},
			enabled: true, // 자동 실행 활성화
			retry: 3, // 실패 시 3번 재시도
			retryDelay: 1000, // 1초 간격으로 재시도
		});

		watch(
			() => faqData.data.value,
			(newData) => {
				console.log("Watch 트리거됨 - newData:", newData);

				if (newData && newData.page && newData.page.list) {
					// FAQ 데이터 매핑
					faqList.value = newData.page.list.map((faq) => ({
						...faq,
						isOpen: false, // 초기 상태는 닫힘
					}));
					total.value = newData.page.total;

					// SEO: 검색 결과에 질문·답변이 노출되도록 FAQPage 구조화 데이터를 넣는다.
					const faqEntities = faqList.value
						.filter((faq) => faq.faqTitle && faq.rmks)
						.slice(0, 30)
						.map((faq) => ({
							"@type": "Question",
							name: stripHtml(faq.faqTitle),
							acceptedAnswer: { "@type": "Answer", text: stripHtml(faq.rmks) },
						}));
					setJsonLd("faq", faqEntities.length ? { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqEntities } : null);

					console.log("FAQ 리스트 업데이트:", {
						listLength: faqList.value.length,
						total: total.value,
						list: faqList.value
					});
				} else {
					console.warn("FAQ 데이터 구조가 예상과 다릅니다:", newData);
					faqList.value = [];
					total.value = 0;
				}
			},
			{ immediate: true } // 즉시 실행
		);

		// 에러 상태 모니터링
		watch(
			() => faqData.isError.value,
			(isError) => {
				if (isError) {
					console.error("FAQ 데이터 로드 에러:", faqData.error.value);
				}
			}
		);

		// 로딩 상태 모니터링
		watch(
			() => faqData.isLoading.value,
			(isLoading) => {
				console.log("FAQ 로딩 상태:", isLoading);
			}
		);

		function routerLink(brandUuid) {
			console.log(brandUuid);
			sessionStorage.setItem("brandUuid", brandUuid);
			router.push("/HFO_BI_002");
		}

		return {
			faqData,
			faqReqData,
			faqList,
			currentPage,
			pageSize,
			routerLink,
			total,
		};
	},
	methods: {
		changeTab(idx) {
			console.log("탭 변경:", idx, this.tab[idx]);

			this.currentTab = idx;
			// 1페이지로 초기화
			this.currentPage = 1;
			this.faqReqData.page = this.currentPage;
			this.faqReqData.faqType = this.tab[idx].cd;

			console.log("변경된 faqReqData:", this.faqReqData);

			// 검색어 초기화 (옵션)
			// this.searchKeyword = "";
			// this.faqReqData.title = "";

			this.faqData.refetch();
		},
		openFaq(index) {
			console.log("FAQ 토글:", index, this.faqList[index]);
			this.faqList[index].isOpen = !this.faqList[index].isOpen;
		},
		searchFaq() {
			console.log("검색 실행:", this.searchKeyword);

			this.faqReqData.title = this.searchKeyword;
			this.currentPage = 1;
			this.faqReqData.page = this.currentPage;

			console.log("검색 faqReqData:", this.faqReqData);

			this.faqData.refetch();
		},
	},
	mounted() {
		console.log("컴포넌트 마운트됨");
		console.log("마운트 시 faqData 상태:", {
			isLoading: this.faqData.isLoading.value,
			isError: this.faqData.isError.value,
			data: this.faqData.data.value
		});

		// 마운트 후 데이터가 없으면 수동으로 refetch
		nextTick(() => {
			if (!this.faqData.data.value && !this.faqData.isLoading.value) {
				console.log("수동 refetch 실행");
				this.faqData.refetch();
			}
		});
	}
};
</script>

<style lang="scss" scoped>
.card_benefit_link {
	margin: 16px 0 0;
	text-align: center;

	a {
		color: #ef7b26;
		font-size: 15px;
		font-weight: 700;
		text-decoration: underline;
	}
}

.loading_area,
.error_area,
.no_data_area {
	text-align: center;
	padding: 40px 20px;

	p {
		margin-bottom: 20px;
		color: #666;
	}

	.btn_retry {
		padding: 8px 16px;
		background: #007bff;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;

		&:hover {
			background: #0056b3;
		}
	}
}
</style>
